// Shared profile and commission data.
// My Profile and the Add/Edit User form both read and write these same fields,
// so they always stay in sync.

// Resize an uploaded image to a small JPEG data URL so it can be stored on the user record.
export const resizeImage = (file, maxSize = 600) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// Formats as 239-555-0100 while typing.
export const formatPhone = (value) => {
  const d = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

export const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Cap anniversary is the first day of the month after the agent's anniversary (start) date.
// Example: July 21 start -> cap anniversary August 1, every year.
export const capAnniversaryFromDate = (dateStr) => {
  const empty = { capAnniversaryMonth: null, capAnniversaryDay: null };
  if (!dateStr) return empty;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return empty;
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return { capAnniversaryMonth: next.getMonth() + 1, capAnniversaryDay: 1 };
};

// Readable version, e.g. "August 1".
export const capAnniversaryLabel = (dateStr) => {
  const c = capAnniversaryFromDate(dateStr);
  if (!c.capAnniversaryMonth) return '';
  return `${MONTHS[c.capAnniversaryMonth - 1]} 1`;
};

const str = (v) => (v === null || v === undefined ? '' : String(v));

// Read the profile fields from a user record (users/{uid}).
export const profileFromRecord = (data = {}) => {
  const p = data.profile || {};
  const parts = String(data.displayName || '').trim().split(' ');
  return {
    firstName: p.firstName || parts[0] || '',
    lastName: p.lastName || parts.slice(1).join(' ') || '',
    title: p.title || '',
    licenseNumber: p.licenseNumber || '',
    phone: p.phone || '',
    contactEmail: p.contactEmail || data.email || '',
    websiteUrl: p.websiteUrl || '',
    googleBusinessUrl: p.googleBusinessUrl || '',
    zoomLink: p.zoomLink || '',
    profilePhotoUrl: p.profilePhotoUrl || '',
    celebrationPhotoUrl: p.celebrationPhotoUrl || '',
    anniversaryDate: p.anniversaryDate || '',
    birthdate: p.birthdate || '',
  };
};

// Turn the profile form into the object saved at users/{uid}/profile.
export const profileToSave = (form) => ({
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  title: form.title.trim(),
  licenseNumber: (form.licenseNumber || '').trim(),
  phone: form.phone.trim(),
  contactEmail: (form.contactEmail || '').trim(),
  websiteUrl: form.websiteUrl.trim(),
  googleBusinessUrl: (form.googleBusinessUrl || '').trim(),
  zoomLink: (form.zoomLink || '').trim(),
  profilePhotoUrl: form.profilePhotoUrl || '',
  celebrationPhotoUrl: form.celebrationPhotoUrl || '',
  anniversaryDate: form.anniversaryDate || '',
  birthdate: form.birthdate || '',
  updatedAt: new Date().toISOString(),
});

// What to write to users/{uid}/profile on save. Starts from what is saved now, so settings stored
// elsewhere on the profile (calling app, etc.) survive, and a photo saved from another screen is never
// wiped by a form that was opened before the photo was uploaded.
const PHOTO_KEYS = ['profilePhotoUrl', 'celebrationPhotoUrl'];
export const mergeProfileForSave = (savedProfile, form) => {
  const next = profileToSave(form);
  PHOTO_KEYS.forEach((k) => { if (!next[k] && savedProfile && savedProfile[k]) next[k] = savedProfile[k]; });
  return { ...(savedProfile || {}), ...next };
};

// Full name used as the user's display name.
export const displayNameFrom = (form) => `${form.firstName.trim()} ${form.lastName.trim()}`.trim();

// Read commission fields (admin only) from a user record.
export const commissionFromRecord = (data = {}) => {
  const c = data.commissionSettings || {};
  return {
    capAnniversaryMonth: str(c.capAnniversaryMonth),
    capAnniversaryDay: str(c.capAnniversaryDay),
    teamSplitPercent: str(c.teamSplitPercent),
    brokerSplitPercent: str(c.brokerSplitPercent),
    capAmount: str(c.capAmount),
    agentLeadPercent: str(c.agentLeadPercent),
    teamLeadPercent: str(c.teamLeadPercent),
  };
};

// Turn the commission form into the object saved at users/{uid}/commissionSettings.
export const commissionToSave = (c) => ({
  capAnniversaryMonth: c.capAnniversaryMonth ? parseInt(c.capAnniversaryMonth, 10) : null,
  capAnniversaryDay: c.capAnniversaryDay ? parseInt(c.capAnniversaryDay, 10) : null,
  // Legacy field, no longer on the forms: keep an existing value, don't write 0 for new users.
  teamSplitPercent: parseFloat(c.teamSplitPercent) || null,
  brokerSplitPercent: parseFloat(c.brokerSplitPercent) || 0,
  capAmount: parseFloat(c.capAmount) || 0,
  agentLeadPercent: parseFloat(c.agentLeadPercent) || 0,
  teamLeadPercent: parseFloat(c.teamLeadPercent) || 0,
});
